/**
 * Story-quest journal state (design/gdd/story-quests.md).
 *
 * Same shape as `vendor-state.ts`: a `createStore` holding the last server
 * response plus request status, with one exported async function per action.
 * Nothing here decides quest logic — accept/turn-in/claim are all server
 * calls, and the board is refetched after each one rather than patched
 * locally.
 *
 * ─── Why every mutation refetches the whole board ───────────────────────────
 * A claim UNLOCKS the next chain step, so the response's own `unlocked` slug
 * is not enough to redraw the panel: the newly-offerable step has a name, a
 * description and a reward the client has never seen. Turn-in is the same —
 * completing the last objective flips `claimable` and empties `outstanding`,
 * and it can complete a DELIVER that the player satisfied from the hideout
 * pool without the client knowing what was in it. Patching either case by hand
 * would mean reimplementing the chain rules client-side, which rule 7's
 * "no client-side branching" already rules out for dialog and which would rot
 * the same way here.
 *
 * The refetch is deliberately awaited inside each action, so `busy` covers the
 * whole round trip and the panel never paints a half-updated board.
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import type { InventorySheet } from './game-state.types';
import type {
  PeriodicQuestBoard, QuestState, StoryBoard, StoryClaimResult, StoryQuestView,
} from './quest-state.types';

const INITIAL_STATE: QuestState = {
  board: null,
  jobs: null, busy: false, error: null, lastResult: null,
};

export const questStateStore = createStore<QuestState>({ ...INITIAL_STATE });

/**
 * Fetches the board.
 *
 * Safe to call repeatedly — it is a plain read, and the marker layer calls it
 * on every settlement entry. `error` is cleared on entry so a transient
 * failure does not stick to a later successful load.
 */
export async function loadQuestBoard(): Promise<void> {
  questStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const board = await api.get<StoryBoard>('/quests/story');
    questStateStore.set((s) => ({ ...s, board, busy: false }));
  } catch (err) {
    questStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t load the journal.') }));
  }
}

/** Accepts a chain step. Idempotent server-side; rejects QUEST_LOCKED if the prerequisite is unclaimed. */
/**
 * The daily/weekly job board.
 *
 * Read separately from the story board because they are separate endpoints and
 * separate failure modes — a job board that 500s must not blank the story
 * quests, which is the half the player is actually following.
 *
 * NOTE: this read has a side effect on the server. `GET /quests/daily` assigns
 * the period's jobs when the player has none, and it is the ONLY path in the
 * system that ever does — core's reset cron re-assigns to players who already
 * hold one and can never bootstrap anybody. Opening the journal is therefore
 * how a new player gets their first errands.
 */
export async function loadJobBoard(): Promise<void> {
  try {
    const jobs = await api.get<PeriodicQuestBoard>('/quests/daily');
    questStateStore.set((s) => ({ ...s, jobs }));
  } catch {
    // Deliberately quiet: the journal's own error line belongs to the story
    // board, and a missing job board is rendered as an empty section.
    questStateStore.set((s) => ({ ...s, jobs: null }));
  }
}

/** Takes the reward for a finished job. Keyed on the UserQuest id, not the slug. */
export async function claimJob(userQuestId: string): Promise<void> {
  questStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const result = await api.post<{ reward: { soft: number; xp: number } }>(`/quests/daily/${userQuestId}/claim`);
    questStateStore.set((s) => ({
      ...s,
      busy: false,
      lastResult: `Paid: ${result.reward.soft} Cash, ${result.reward.xp} XP.`,
    }));
    await loadJobBoard();
  } catch (err) {
    questStateStore.set((s) => ({
      ...s, busy: false, error: err instanceof Error ? err.message : 'Couldn’t take that reward.',
    }));
  }
}

export async function acceptQuest(slug: string): Promise<void> {
  await mutate(slug, async () => {
    await api.post<{ assigned: boolean }>(`/quests/story/${slug}/accept`, {});
    return 'Accepted.';
  }, 'Couldn\'t accept that job.');
}

/**
 * Hands in every DELIVER objective at once.
 *
 * All-or-nothing server-side: short by one item and it consumes nothing and
 * throws ITEMS_MISSING, which surfaces here as the error line. That is why the
 * panel shows `outstanding` up front — so the player is not guessing.
 */
export async function turnInQuest(slug: string): Promise<void> {
  await mutate(slug, async () => {
    const view = await api.post<StoryQuestView>(`/quests/story/${slug}/turn-in`, {});
    return view.claimable
      ? 'Handed over. Ready to collect.'
      : `Handed over — ${view.completed}/${view.total} done.`;
  }, 'Couldn\'t hand that in.');
}

/** Claims a completed step's reward and unlocks the next one. */
export async function claimQuest(slug: string): Promise<void> {
  await mutate(slug, async () => {
    const result = await api.post<StoryClaimResult>(`/quests/story/${slug}/claim`, {});
    const parts = [`+${result.reward.soft} Cash`, `+${result.reward.xp} XP`];
    if (result.reward.reputation) parts.push(`+${result.reward.reputation} rep`);
    for (const item of result.reward.items) parts.push(`${item.qty}x ${itemLabel(item.itemKey)}`);
    return parts.join(' · ');
  }, 'Couldn\'t collect that reward.');
}

/**
 * Abandons an active step.
 *
 * Returns 204 with an empty body — axios hands back `''`, which is why this is
 * typed `void` rather than parsed. The step stays re-offerable from its giver
 * (the server flips it to EXPIRED, and `questNotStarted` treats EXPIRED as
 * not-started), so the board must be refetched to bring the offer back.
 */
export async function cancelQuest(slug: string): Promise<void> {
  await mutate(slug, async () => {
    await api.post<void>(`/quests/story/${slug}/cancel`, {});
    return 'Abandoned.';
  }, 'Couldn\'t abandon that job.');
}

export function resetQuestState(): void {
  questStateStore.set({ ...INITIAL_STATE });
}

/**
 * Shared action wrapper: hold `busy` across the call AND the refetch, keep the
 * receipt on success, and never leave `busy` set on a throw.
 *
 * The refetch runs even when the action failed — an ITEMS_MISSING turn-in
 * means the client's idea of `outstanding` was stale, so re-reading is exactly
 * what the player needs to see next.
 */
async function mutate(
  slug: string,
  action: () => Promise<string>,
  fallback: string,
): Promise<void> {
  if (!slug || questStateStore.get().busy) return;
  questStateStore.set((s) => ({ ...s, busy: true, error: null, lastResult: null }));
  let result: string | null = null;
  let error: string | null = null;
  try {
    result = await action();
  } catch (err) {
    error = msg(err, fallback);
  }
  try {
    const board = await api.get<StoryBoard>('/quests/story');
    questStateStore.set((s) => ({ ...s, board, busy: false, error, lastResult: result }));
  } catch (err) {
    questStateStore.set((s) => ({
      ...s, busy: false, lastResult: result, error: error ?? msg(err, 'Couldn\'t refresh the journal.'),
    }));
  }
}

export interface HandOverGate {
  enabled: boolean;
  /** Why the button is locked, in player words. Null when it is enabled. */
  reason: string | null;
}

/**
 * Whether "Hand over" can do anything right now.
 *
 * Turn-in consumes only what sits in the HIDEOUT pool and takes DELIVER
 * objectives only (TALK objectives never block it and it never completes them),
 * so that is exactly what is checked here. Items still in the carried bag are
 * reported as such rather than counted: the server would refuse them.
 *
 * Counts come from the live inventory sheet when it is loaded, because the
 * board's `held` is only as fresh as the last board fetch. Without an
 * inventory it falls back to `held`. The server stays the authority either
 * way - this only decides whether the button is worth offering.
 */
export function handOverGate(quest: StoryQuestView, inventory: InventorySheet | null): HandOverGate {
  if (quest.outstanding.length === 0) {
    const todo = quest.objectives.filter((o) => !o.complete).map((o) => o.description);
    return {
      enabled: false,
      reason: todo.length > 0
        ? `Nothing to hand over yet. Still to do: ${todo.join(', ')}`
        : 'Nothing to hand over.',
    };
  }

  const count = (rows: InventorySheet['hideout'] | undefined, key: string): number =>
    (rows ?? []).reduce((n, r) => (r.itemId === key ? n + r.quantity : n), 0);

  const missing: string[] = [];
  let inBag = false;
  for (const o of quest.outstanding) {
    const held = inventory ? count(inventory.hideout, o.itemKey) : o.held;
    const short = o.needed - Math.min(o.needed, held);
    if (short <= 0) continue;
    missing.push(`${short}x ${itemLabel(o.itemKey)}`);
    if (inventory && count(inventory.carried, o.itemKey) > 0) inBag = true;
  }
  if (missing.length === 0) return { enabled: true, reason: null };
  return {
    enabled: false,
    reason: `Missing: ${missing.join(', ')}`
      + (inBag ? '. Items in your bag must be stored at the hideout before they can be handed over.' : ''),
  };
}

/** `first_aid_bag` -> `First aid bag`. The board carries keys, not display names. */
export function itemLabel(itemKey: string): string {
  const words = itemKey.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function msg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
